import axios from 'axios';
import { isFeatureEnabled } from '../jsx/components/Util/featureFlags';

/*
 * Pagine parlanti: il testo della colonna destra delle pagine della Struttura, scritto dal super admin
 * (menu «Pagine parlanti»). Una pagina parlante = { id, title, url, html, status, updatedAt }.
 * `url` è il percorso della pagina senza la barra iniziale; i segmenti che cambiano si scrivono «:id»
 * (es. «struttura/booking/courses/:id»), come in layouts/pageMeta.js.
 *
 * SIMULAZIONE: finché il backend non ha le rotte (docs/richieste-backend.md, capitolo 5) i dati stanno
 * nel localStorage di questo browser, così si prova tutto il giro: il super admin scrive, la Struttura
 * (nello stesso browser) vede la colonna. Con REACT_APP_PAGINE_PARLANTI_BACKEND=S si usano le rotte vere.
 */
export const PAGINE_PARLANTI_SIMULAZIONE =
    !isFeatureEnabled(process.env.REACT_APP_PAGINE_PARLANTI_BACKEND ?? 'N');

export const STATI_PAGINA = [
    { value: 'PUBLISHED', label: 'Pubblicata', tono: 'attivo' },
    { value: 'DRAFT', label: 'Bozza', tono: 'non-attivo' },
];
export const etichettaStato = (status) => STATI_PAGINA.find((s) => s.value === status)?.label || status;

const CHIAVE = 'sid-pagine-parlanti-simulazione';

const leggiSimulazione = () => {
    try {
        const salvate = JSON.parse(localStorage.getItem(CHIAVE) || 'null');
        if (Array.isArray(salvate)) return salvate;
    } catch { /* storage non disponibile */ }
    return [];
};
const scriviSimulazione = (pagine) => {
    try { localStorage.setItem(CHIAVE, JSON.stringify(pagine)); } catch { /* storage non disponibile */ }
    window.dispatchEvent(new Event('sid-pagine-parlanti'));
};
const nuovoId = () => `pp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** Normalizza l'URL scritto dal super admin: senza dominio, senza barre agli estremi, senza ? e #. */
export const pulisciUrl = (url) =>
    String(url || '').trim().replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');

const urlDuplicato = (pagine, url, id) => pagine.find((p) => p.url === url && p.id !== id);
const messaggioDuplicato = (doppia) => `Esiste già una pagina parlante per questo indirizzo: «${doppia.title}».`;

const token = () => {
    try { return JSON.parse(sessionStorage.getItem('userDetails')).idToken; } catch { return ''; }
};
const basePath = () => String(process.env.REACT_APP_API_BASE_PATH || '').replace(/\/+$/, '');
const chiama = async (method, path, data) => {
    try {
        const r = await axios.request({ method, url: `${basePath()}${path}`, data, headers: { authorization: token(), accept: 'application/json', 'Content-Type': 'application/json' } });
        return r.data;
    } catch (e) {
        return { error: e.response?.data?.detail || e.response?.data?.message || 'Errore generico', status: e.response?.status };
    }
};

/* ── Super admin ─────────────────────────────────────────────── */

export const elencoPagineParlanti = async () => {
    if (PAGINE_PARLANTI_SIMULAZIONE) return leggiSimulazione();
    const r = await chiama('get', '/administration/talkingPages');
    return r?.error ? r : (r.items || r);
};

export const leggiPaginaParlante = async (id) => {
    if (PAGINE_PARLANTI_SIMULAZIONE) {
        return leggiSimulazione().find((p) => p.id === id) || { error: 'Pagina parlante non trovata.', status: 404 };
    }
    return chiama('get', `/administration/talkingPages/${encodeURIComponent(id)}`);
};

export const creaPaginaParlante = async ({ title, url, html, status }) => {
    const dati = { title: title.trim(), url: pulisciUrl(url), html, status };
    if (PAGINE_PARLANTI_SIMULAZIONE) {
        const pagine = leggiSimulazione();
        const doppia = urlDuplicato(pagine, dati.url);
        if (doppia) return { error: messaggioDuplicato(doppia), status: 409 };
        const creata = { ...dati, id: nuovoId(), updatedAt: new Date().toISOString() };
        scriviSimulazione([creata, ...pagine]);
        return creata;
    }
    return chiama('post', '/administration/talkingPages', dati);
};

export const aggiornaPaginaParlante = async (id, modifiche) => {
    const dati = { ...modifiche };
    if (dati.title !== undefined) dati.title = dati.title.trim();
    if (dati.url !== undefined) dati.url = pulisciUrl(dati.url);
    if (PAGINE_PARLANTI_SIMULAZIONE) {
        const pagine = leggiSimulazione();
        const attuale = pagine.find((p) => p.id === id);
        if (!attuale) return { error: 'Pagina parlante non trovata.', status: 404 };
        if (dati.url !== undefined) {
            const doppia = urlDuplicato(pagine, dati.url, id);
            if (doppia) return { error: messaggioDuplicato(doppia), status: 409 };
        }
        const aggiornata = { ...attuale, ...dati, updatedAt: new Date().toISOString() };
        scriviSimulazione(pagine.map((p) => (p.id === id ? aggiornata : p)));
        return aggiornata;
    }
    return chiama('patch', `/administration/talkingPages/${encodeURIComponent(id)}`, dati);
};

export const eliminaPaginaParlante = async (id) => {
    if (PAGINE_PARLANTI_SIMULAZIONE) {
        scriviSimulazione(leggiSimulazione().filter((p) => p.id !== id));
        return {};
    }
    return chiama('delete', `/administration/talkingPages/${encodeURIComponent(id)}`);
};

/* ── Struttura: solo le pubblicate, senza bozze ──────────────── */

export const paginePubblicate = async () => {
    if (PAGINE_PARLANTI_SIMULAZIONE) {
        return leggiSimulazione().filter((p) => p.status === 'PUBLISHED').map(({ url, title, html, updatedAt }) => ({ url, title, html, updatedAt }));
    }
    const r = await chiama('get', '/talkingPages/published');
    return r?.error ? [] : (r.items || r);
};
